/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import _ from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';

import SyncModal from './sync-modal';
import Icon from '@components/icon-component';

import useNetworkStatus from '@hooks/useNetworkStatus';
import CacheService from '@services/cache.service';
import { cacheActions } from '@redux/slices/cache';
import useWindowDimensions from '@hooks/useWindowDimensions';

import style from './indicator.module.scss';
import LocalStorage from '@utilities/local-storage.util';
import { tasksListsActions } from '@redux/slices/tasks';
import { timeToSeconds } from '@utilities/date-filters';
import { settingsActions } from '@redux/slices/settings';
import checkTokenIsGoingToExpire from '@utilities/token-expiry-check.util';
import LoginExpiredModal from '@components/modal/variants/login-expired/login-expired-modal';
import { isOneHourLater } from '@utilities/date-functions';

const OnlineOfflineIndicator = ({ helpIcon, isCaching }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [isOnline] = useNetworkStatus();
  const { width } = useWindowDimensions();

  const [isOpen, setIsOpen] = useState(false);
  const [showSnoozeOption, setShowSnoozeOption] = useState(false);
  const [scheduleTimeLimitExceeds, setScheduleTimeLimitExceeds] = useState(false);
  const [openTokenExpireModal, setOpenTokenExpireModal] = useState(false);

  const { pendingSync = 0, prevPendingSync = 0, equipments } = useSelector((state) => state.cache);
  const { tasksList = [], syncFailedAssets } = useSelector((state) => state.tasks);
  const HSEEventList = useSelector((state) => state.hseEvents.HSEEventList) || [];
  const checksList = useSelector((state) => state.checks.checksList) || [];
  const inspectionsList = useSelector((state) => state.inspection.inspectionsList) || [];
  const { syncReminder } = useSelector((state) => state.cache);
  const {
    lastSyncReminder,
    syncReminderFrequency,
    lastSync,
    settings = {},
    isSettingUpdated,
    snoozeTimeStartTime,
    tokenExpireModalTime
  } = useSelector((state) => state.settings);

  const {
    pendingSyncTasks,
    pendingSyncInspection,
    pendingSyncHSEEventList,
    pendingSyncChecksList,
    pendingSyncEquipments
  } = useMemo(() => {
    const pendingSyncTasks = tasksList?.filter((task) => task.status === 'Pending sync');
    const pendingSyncEquipments =
      equipments?.filter((equipment) => equipment.equipmentEditingStatus === 'Pending sync') || [];
    const pendingSyncInspection = inspectionsList?.filter((ins) => ins.status === 'Pending sync');
    const pendingSyncHSEEventList = HSEEventList?.filter((ins) => ins.status === 'Pending sync');
    const pendingSyncChecksList = checksList?.filter((ins) => ins.status === 'Pending sync');
    return {
      pendingSyncTasks,
      pendingSyncInspection,
      pendingSyncHSEEventList,
      pendingSyncChecksList,
      pendingSyncEquipments
    };
  }, [tasksList, inspectionsList, HSEEventList, pendingSync, equipments]);

  useEffect(() => {
    const startSyncingOrRefreshing = localStorage.getItem('startSyncingOrRefreshing');
    // check if we syncing  or refresh then  network is lost, then show modal of offline
    if (isOnline !== true && startSyncingOrRefreshing === 'true') {
      setIsOpen(true);
    }
  }, [isOnline, localStorage.getItem('startSyncingOrRefreshing')]);

  useEffect(() => {
    if (!isOpen) {
      localStorage.setItem('startSyncingOrRefreshing', false);
    }
  }, [isOpen]);

  useEffect(() => {
    const sum =
      _.size(pendingSyncTasks) +
      _.size(pendingSyncInspection) +
      _.size(pendingSyncHSEEventList) +
      _.size(pendingSyncChecksList) +
      _.size(pendingSyncEquipments);
    CacheService.setPendingSyncTasksNumber(sum);

    dispatch?.(cacheActions.setPrevPendingSync(sum));
  }, [
    pendingSyncTasks,
    pendingSyncInspection,
    pendingSyncHSEEventList,
    pendingSync,
    pendingSyncChecksList,
    pendingSyncEquipments
  ]);

  useEffect(() => {
    const startSyncingOrRefreshing = localStorage.getItem('startSyncingOrRefreshing');

    let isFormOpen = false;
    if (location.pathname.includes('/work-order')) isFormOpen = true;
    else isFormOpen = false;
    let connection = 'wifi';

    if ('connection' in navigator) connection = navigator.connection.type || 'wifi';

    const allPendingSyncTasks =
      _.size(pendingSyncTasks) +
      _.size(pendingSyncInspection) +
      _.size(pendingSyncHSEEventList) +
      _.size(pendingSyncEquipments) +
      _.size(pendingSyncChecksList);

    if (isOnline !== true && startSyncingOrRefreshing === 'false') {
      setIsOpen(false);
    } else if (
      ((settings.autoConnectPromptSyncConnection === 'wifi' && connection === 'wifi') ||
        settings.autoConnectPromptSyncConnection === 'any') &&
      isFormOpen === false &&
      allPendingSyncTasks > prevPendingSync &&
      syncReminder !== true &&
      !isCaching
    ) {
      if (allPendingSyncTasks > prevPendingSync) {
        setShowSnoozeOption(true);
      }

      const tokenIsExpire = checkTokenIsGoingToExpire();

      if (snoozeTimeStartTime) {
        const diffInSeconds =
          timeToSeconds({ time: moment().format('HH:mm:ss') }) -
          timeToSeconds({ time: snoozeTimeStartTime });
        let diffInHours = diffInSeconds / 3600;
        diffInHours = parseInt(diffInHours);
        if (diffInHours >= 1) {
          if (!tokenIsExpire.isValid && navigator.onLine) {
            if (tokenExpireModalTime) {
              let oneHourIsDone = isOneHourLater(tokenExpireModalTime);
              if (oneHourIsDone) {
                setOpenTokenExpireModal(true);
              }
            } else {
              setOpenTokenExpireModal(true);
            }
          } else {
            setIsOpen(true);
            dispatch?.(settingsActions.setSnoozeTimeStartTime(null));
          }
        }
      } else {
        if (!tokenIsExpire.isValid && navigator.onLine) {
          if (tokenExpireModalTime) {
            let oneHourIsDone = isOneHourLater(tokenExpireModalTime);
            if (oneHourIsDone) {
              setOpenTokenExpireModal(true);
            }
          } else {
            setOpenTokenExpireModal(true);
          }
        } else {
          setIsOpen(true);
        }
      }
    }
  }, [
    isOnline,
    pendingSyncTasks,
    pendingSyncInspection,
    pendingSyncHSEEventList,
    pendingSyncChecksList,
    pendingSyncEquipments,
    settings,
    snoozeTimeStartTime,
    dispatch,
    tokenExpireModalTime
  ]);

  useEffect(() => {
    if (isOpen) {
      dispatch?.(cacheActions?.updateSyncingReminder(true));
      dispatch?.(cacheActions?.closeCachingComplete(false));

      if (snoozeTimeStartTime) {
        const diffInSeconds =
          timeToSeconds({ time: moment().format('HH:mm:ss') }) -
          timeToSeconds({ time: snoozeTimeStartTime });
        let diffInHours = diffInSeconds / 3600;
        diffInHours = parseInt(diffInHours.toFixed());
        if (diffInHours >= 1) {
          dispatch?.(settingsActions.setSnoozeTimeStartTime(null));
        }
      }
    } else {
      dispatch?.(cacheActions?.updateSyncingReminder(false));
    }
  }, [isOpen, snoozeTimeStartTime]);

  useEffect(() => {
    const lastSyncReminderTime = moment(lastSync);
    const { syncReminderNum, syncReminderTimeframe } = settings;

    let timePassedByDaysOrHours;
    if (syncReminderTimeframe === 'days') {
      timePassedByDaysOrHours = moment().subtract(syncReminderNum, 'days');
    } else {
      timePassedByDaysOrHours = moment().subtract(syncReminderNum, 'hours');
    }

    const exceedTimeLimit = lastSyncReminderTime.isBefore(timePassedByDaysOrHours);
    lastSync && setScheduleTimeLimitExceeds(exceedTimeLimit);
    if (syncReminderFrequency > 0 && syncReminder !== true && !isOpen && !isCaching) {
      dispatch?.(cacheActions?.updateSyncingReminder(true));

      const tokenIsExpire = checkTokenIsGoingToExpire();

      if (!tokenIsExpire.isValid && navigator.onLine) {
        setOpenTokenExpireModal(true);
      } else {
        setIsOpen(true);
      }
    }
  }, [syncReminderFrequency, lastSync]);

  const dashboard = useMemo(() => {
    return width < 768 && (location.pathname === '/dashboard' || location.pathname === '/error');
  }, [width]);

  useEffect(() => {
    if (tokenExpireModalTime) {
      let oneHourIsDone = isOneHourLater(tokenExpireModalTime);
      if (oneHourIsDone) {
        setOpenTokenExpireModal(true);
      }
    }
  }, [tokenExpireModalTime]);

  const showRedIcon = useMemo(() => {
    return scheduleTimeLimitExceeds ||
      isSettingUpdated ||
      syncFailedAssets?.photos?.length > 0 ||
      syncFailedAssets?.documents?.length > 0
      ? true
      : false;
  }, [syncFailedAssets, scheduleTimeLimitExceeds, isSettingUpdated]);

  const clearAssetsCache = async () => {
    const tokenIsExpire = checkTokenIsGoingToExpire();
    if (!tokenIsExpire.isValid && navigator.onLine) {
      setOpenTokenExpireModal(true);
    } else {
      setShowSnoozeOption(false);
      let syncFailedAssetsPhotos =
        syncFailedAssets?.photos?.length > 0 ? [...syncFailedAssets.photos] : [];
      let syncFailedAssetsDocuments =
        syncFailedAssets?.documents?.length > 0 ? [...syncFailedAssets.documents] : [];

      if (syncFailedAssetsPhotos?.length > 0 || syncFailedAssetsDocuments?.length > 0) {
        for (let x of syncFailedAssetsPhotos) {
          const response = await LocalStorage.getItem(x?.content?.id);

          if (!response) {
            console.log('remove from cache');
            syncFailedAssetsPhotos = syncFailedAssetsPhotos.filter(
              (photo) => photo.content.id !== x.content.id
            );
          }
        }

        for (let x of syncFailedAssetsDocuments) {
          const response = await LocalStorage.getItem(x?.content?.id);

          if (!response) {
            console.log('remove from cache');
            syncFailedAssetsDocuments = syncFailedAssetsDocuments.filter(
              (photo) => photo.content.id !== x.content.id
            );
          }
        }

        dispatch?.(
          tasksListsActions.setSyncFailedAssets({
            photos: syncFailedAssetsPhotos,
            documents: syncFailedAssetsDocuments
          })
        );
        setTimeout(() => {
          setIsOpen((prev) => !prev);
        }, 100);
      } else {
        setIsOpen((prev) => !prev);
      }
    }
  };

  return (
    <>
      <div
        className={`${style.iconDiv} ${dashboard ? style.dashboard : style.notDashboard}`}
        onClick={() => clearAssetsCache()}
      >
        <div className={style.icon}>
          <div
            className={style.iconCircle}
            style={{ borderColor: isOnline ? '#2F80ED' : '#CBD2D9' }}
          >
            <Icon
              name={isOnline ? 'newOnline' : 'newOffline'}
              height={20}
              width={20}
              className={style.roundIcon}
            />
          </div>
          {helpIcon && <Icon name={'help-red'} height={14} width={14} className={style.helpIcon} />}
          {showRedIcon ? (
            <span
              className={style.exclamationMark}
              style={{
                background: isOnline ? '#E02B2B' : ''
              }}
            >
              !
            </span>
          ) : (
            ''
          )}
          {pendingSync ? (
            <span
              className={style.count}
              style={{
                background: isOnline ? '#2F80ED' : ''
              }}
            >
              {pendingSync}
            </span>
          ) : (
            ''
          )}
        </div>
        <p className={style.title}> {isOnline ? 'ONLINE' : 'OFFLINE'}</p>
      </div>

      {(isOpen || isCaching) && (
        <SyncModal
          isOnline={isOnline}
          isOpen={isOpen}
          setIsOpen={(value) => {
            setIsOpen(value);
            setShowSnoozeOption(false);
          }}
          limitExceeded={scheduleTimeLimitExceeds}
          isSettingUpdated={isSettingUpdated}
          showSnoozeOption={showSnoozeOption}
        />
      )}

      <LoginExpiredModal
        open={openTokenExpireModal}
        setOpen={() => {
          if (syncReminderFrequency) {
            dispatch?.(settingsActions.setSyncReminderFrequency(0));
            dispatch?.(cacheActions.updateSyncingReminder(false));
            dispatch?.(settingsActions.setLastSynReminder(moment().format()));
          }

          setOpenTokenExpireModal(false);
          dispatch?.(
            settingsActions.setTokenExpireModalTime(moment().format('YYYY-MM-DD HH:mm:ss'))
          );
        }}
      />
    </>
  );
};

export default React.memo(OnlineOfflineIndicator, _.isEqual);
